import { createContext, useContext, useState, useCallback, useRef } from "react";

const ToastContext = createContext(null);

const TOAST_LIMIT = 5;
const TOAST_DURATION = 5000; // 5 seconds auto-dismiss

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const addToast = useCallback(
    ({ type = "info", title, description, duration = TOAST_DURATION }) => {
      const id = `toast-${++toastId}`;

      setToasts((prev) => {
        const next = [...prev, { id, type, title, description }];
        // Keep only the latest TOAST_LIMIT toasts
        return next.slice(-TOAST_LIMIT);
      });

      // Auto-dismiss
      if (duration > 0) {
        const timeout = setTimeout(() => removeToast(id), duration);
        timeoutsRef.current.set(id, timeout);
      }

      return id;
    },
    [removeToast]
  );

  const dismissToast = useCallback(
    (id) => {
      if (id) {
        removeToast(id);
      } else {
        // Dismiss all
        setToasts([]);
        timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
        timeoutsRef.current.clear();
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
